import React from 'react'
import { UserService } from '@duck/api-definitions'
import { Checkbox, CopyableText, cn } from '@duck/ui'
import { toast } from 'sonner'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'

export interface PersonRowModel {
  person: UserService.Person
  totalAssignments: number
  submittedAssignments: number
  bestGrade?: number
}

interface PersonRowProps {
  model: PersonRowModel
  checked: boolean
  onToggle: (checked: boolean) => void
}

const statusPill = (status: UserService.PersonOnboardingStatusEnum) => {
  switch (status) {
    case 'joined': return 'border-success/40 bg-success/10 text-success'
    case 'invited': return 'border-amber-500/40 bg-amber-500/10 text-amber-500'
    default: return 'border-border bg-muted/40 text-muted-foreground'
  }
}

export const PersonRow: React.FC<PersonRowProps> = ({ model, checked, onToggle }) => {
  const { t } = useTar()
  const { person, totalAssignments, submittedAssignments, bestGrade } = model
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email
  const initials = `${person.firstName?.charAt(0) || ''}${person.lastName?.charAt(0) || ''}`.toUpperCase()
  const pct = totalAssignments > 0 ? (submittedAssignments / totalAssignments) * 100 : 0

  const copyInvite = async () => {
    if (!person.invitationToken) return
    const link = `${window.location.origin}/?token=${person.invitationToken}`
    try { await navigator.clipboard.writeText(link); toast.success(t('common.linkCopied')) }
    catch { toast.error(t('common.failedToCopyLink') + '\n' + link, { duration: 15000 }) }
  }

  return (
    <div className="grid min-h-[60px] grid-cols-[26px_1fr_auto] items-center gap-3 border-b border-border/60 px-3.5 last:border-b-0 hover:bg-muted/30 lg:h-[60px] lg:grid-cols-[26px_1.5fr_1.4fr_140px_1fr]">
      <Checkbox aria-label={t('partner.selectPerson', { name })} checked={checked} onCheckedChange={(c) => onToggle(!!c)} className="h-4 w-4" />
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{initials}</span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-foreground">{name}</span>
          {/* Narrow viewports (below lg): email under the name; the dedicated Email column is hidden. */}
          {person.email && <span className="truncate text-xs text-muted-foreground lg:hidden">{person.email}</span>}
        </div>
      </div>
      <div className="hidden min-w-0 lg:block">
        {person.email && <CopyableText value={person.email} className="truncate text-xs text-muted-foreground">{person.email}</CopyableText>}
      </div>
      <div className="hidden items-center gap-2 lg:flex">
        <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs', statusPill(person.onboardingStatus))}>
          {t(`partner.onboarding_${person.onboardingStatus}` as TranslationKey)}
        </span>
        {person.onboardingStatus === 'invited' && person.invitationToken && (
          <button type="button" onClick={copyInvite} className="text-xs text-primary hover:underline">{t('common.copyLink')}</button>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span aria-hidden="true" className="hidden h-1.5 w-14 overflow-hidden rounded-full bg-muted lg:block">
          <span className="block h-full bg-primary" style={{ width: `${pct}%` }} />
        </span>
        <span className="hidden lg:inline">{submittedAssignments}/{totalAssignments}</span>
        {bestGrade !== undefined ? (
          <GradeBadge grade={bestGrade} small />
        ) : (
          <>
            {/* Desktop keeps a plain placeholder — the dedicated Status column already carries the pill. */}
            <span className="hidden text-muted-foreground/70 lg:inline">{t('partner.noGrade')}</span>
            {/* Mobile has no Status column, so an ungraded row surfaces the onboarding status here instead. */}
            <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs lg:hidden', statusPill(person.onboardingStatus))}>
              {t(`partner.onboarding_${person.onboardingStatus}` as TranslationKey)}
            </span>
          </>
        )}
      </div>
    </div>
  )
}
